import { MatMenuTrigger } from '@angular/material/menu';
import { LogService } from '../../services/log-service';

const VIEWPORT_MARGIN = 8;
const PATCHED_FLAG = '__fo_adaptive_submenu_patched__';
// Fake "class" context so LogService's `clazz.constructor.name` prefix is readable.
const LOG_CONTEXT = { constructor: { name: 'AdaptiveSubmenuPosition' } };

/**
 * Angular Material's `mat-menu` computes a single fixed fallback position for
 * *nested* submenus (unlike top-level menus, which fully flip to fit the viewport).
 * When a submenu trigger sits near a screen edge, the CDK overlay just shrinks the
 * panel (flexible-dimensions + push) instead of flipping side, producing the
 * clipped/scrolling submenus seen across the app.
 *
 * This monkey-patches the shared `MatMenuTriggerBase._openMenu` once, app-wide, so
 * every *submenu* (any menu opened from inside another menu, whether via hover,
 * click or keyboard) picks the xPosition/yPosition with the most available viewport
 * space right before it opens - with no per-component change needed. Top-level menus
 * (which already set `xPosition`/`yPosition` explicitly per component) are untouched.
 */
export function installAdaptiveSubmenuPositioning(): void {
  // Submenus normally open on hover, which calls the protected `_openMenu()` on the
  // shared `MatMenuTriggerBase` directly - NOT the public `MatMenuTrigger.openMenu()`.
  // `MatMenuTriggerBase` isn't part of the public API, so grab its prototype at runtime.
  const basePrototype = Object.getPrototypeOf(MatMenuTrigger.prototype) as Record<string, unknown> & {
    _openMenu: (autoFocus: boolean) => void;
    _triggersSubmenu: () => boolean;
  };
  if (basePrototype[PATCHED_FLAG]) {
    LogService.debug(LOG_CONTEXT, 'installAdaptiveSubmenuPositioning', 'already patched, skipping', null);
    return;
  }
  basePrototype[PATCHED_FLAG] = true;

  const originalOpenMenu = basePrototype._openMenu;

  basePrototype._openMenu = function (this: MatMenuTrigger & { _triggersSubmenu: () => boolean }, autoFocus: boolean): void {
    const isSubmenu = typeof window !== 'undefined' && this._triggersSubmenu();
    if (isSubmenu) {
      adjustSubmenuPosition(this);
    }
    originalOpenMenu.call(this, autoFocus);
    if (isSubmenu) {
      // CDK computes the `bottom`/`right` exact-position CSS using document (not
      // viewport) dimensions, so on a scrollable page it can still render off-screen
      // even when our xPosition/yPosition choice was correct - clamp it as a safety net.
      requestAnimationFrame(() => clampSubmenuWithinViewport(this));
    }
  };

  LogService.debug(LOG_CONTEXT, 'installAdaptiveSubmenuPositioning', 'patch installed', null);
}

export function adjustSubmenuPosition(trigger: MatMenuTrigger): void {
  const hostElement = (trigger as unknown as { _element?: { nativeElement: HTMLElement } })._element?.nativeElement;
  const menu = trigger.menu as unknown as { xPosition?: string; yPosition?: string } | null;

  if (!hostElement || typeof menu?.xPosition !== 'string' || typeof menu?.yPosition !== 'string') {
    LogService.debug(LOG_CONTEXT, 'adjustSubmenuPosition', 'skipped: missing host element or menu instance', {
      hasHostElement: !!hostElement,
      menu,
    });
    return;
  }

  const rect = hostElement.getBoundingClientRect();
  const spaceLeft = rect.left - VIEWPORT_MARGIN;
  const spaceRight = window.innerWidth - rect.right - VIEWPORT_MARGIN;
  const spaceAbove = rect.top - VIEWPORT_MARGIN;
  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;

  menu.xPosition = spaceRight >= spaceLeft ? 'after' : 'before';
  menu.yPosition = spaceBelow >= spaceAbove ? 'below' : 'above';

  LogService.debug(LOG_CONTEXT, 'adjustSubmenuPosition', 'submenu repositioned', {
    spaceLeft,
    spaceRight,
    spaceAbove,
    spaceBelow,
    xPosition: menu.xPosition,
    yPosition: menu.yPosition,
  });
}

export function clampSubmenuWithinViewport(trigger: MatMenuTrigger): void {
  const overlayRef = (trigger as unknown as { _overlayRef?: { overlayElement?: HTMLElement } })._overlayRef;
  const pane = overlayRef?.overlayElement;
  if (!pane) {
    return;
  }

  const rect = pane.getBoundingClientRect();
  const adjustments: Record<string, number> = {};

  const overflowBottom = rect.bottom - (window.innerHeight - VIEWPORT_MARGIN);
  if (overflowBottom > 0 && pane.style.bottom) {
    adjustments['bottom'] = (parseFloat(pane.style.bottom) || 0) + overflowBottom;
  }
  const overflowTop = VIEWPORT_MARGIN - rect.top;
  if (overflowTop > 0 && pane.style.top) {
    adjustments['top'] = (parseFloat(pane.style.top) || 0) + overflowTop;
  }
  const overflowRight = rect.right - (window.innerWidth - VIEWPORT_MARGIN);
  if (overflowRight > 0 && pane.style.right) {
    adjustments['right'] = (parseFloat(pane.style.right) || 0) + overflowRight;
  }
  const overflowLeft = VIEWPORT_MARGIN - rect.left;
  if (overflowLeft > 0 && pane.style.left) {
    adjustments['left'] = (parseFloat(pane.style.left) || 0) + overflowLeft;
  }

  if (Object.keys(adjustments).length === 0) {
    return;
  }

  Object.entries(adjustments).forEach(([prop, value]) => {
    pane.style.setProperty(prop, `${value}px`);
  });

  LogService.debug(LOG_CONTEXT, 'clampSubmenuWithinViewport', 'submenu clamped back into viewport', {
    before: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right },
    adjustments,
  });
}
